/**
 * Authenticated API Client for Project Adya
 * Handles HTTP requests with automatic token management and refresh
 */

const DEFAULT_API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';
const API_BASE_URL = process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL;

interface TokenData {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface EmailTemplateData {
  key: string
  subject: string
  body_text: string
  body_html: string
  updated_at: string
}

class ApiClient {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private effectiveRole: string | null = null;

  constructor() {
    // Load tokens from localStorage
    this.loadTokens();
  }

  private loadTokens(): void {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    this.effectiveRole = localStorage.getItem('effective_role');
  }

  public getEffectiveRole(): string | null {
    return this.effectiveRole;
  }

  public setEffectiveRole(role: string | null): void {
    const normalized = (role || '').trim().toLowerCase();
    if (!normalized || normalized === 'admin') {
      this.effectiveRole = null;
      localStorage.removeItem('effective_role');
      return;
    }

    this.effectiveRole = normalized;
    localStorage.setItem('effective_role', normalized);
  }

  private saveTokens(data: TokenData): void {
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
  }

  public clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    this.effectiveRole = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('effective_role');
  }

  public isAuthenticated(): boolean {
    return this.accessToken !== null;
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: this.refreshToken,
        }),
      });

      if (!response.ok) {
        this.clearTokens();
        return false;
      }

      const data: TokenData = await response.json();
      this.saveTokens(data);
      return true;
    } catch (error) {
      this.clearTokens();
      return false;
    }
  }

  public async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Add auth header if we have a token
    const headers = new Headers(options.headers || {});
    headers.set('Content-Type', 'application/json');

    if (this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    if (this.effectiveRole) {
      headers.set('X-Adya-Effective-Role', this.effectiveRole);
    }

    let response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    // If 401, try to refresh token and retry once
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed && this.accessToken) {
        headers.set('Authorization', `Bearer ${this.accessToken}`);
        response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers,
        });
      }
    }

    // If still 401, clear tokens and redirect to login
    if (response.status === 401) {
      this.clearTokens();
      window.location.href = '/login';
      throw new Error('Authentication required');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return undefined as unknown as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as unknown as T;
    }

    return JSON.parse(text) as T;
  }

  public async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  public async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  public async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  public async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  public async postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {};
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    if (this.effectiveRole) {
      headers['X-Effective-Role'] = this.effectiveRole;
    }
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    const text = await response.text();
    if (!text) return undefined as unknown as T;
    return JSON.parse(text) as T;
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Auth-specific methods
  public async login(email: string, password: string): Promise<any> {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    const data = await response.json();
    this.saveTokens(data);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  public async acceptInvite(token: string, password: string, fullName?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/auth/accept-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        password,
        full_name: fullName || null,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Invite acceptance failed' }))
      throw new Error(error.detail || 'Invite acceptance failed')
    }

    const data = await response.json()
    this.saveTokens(data)
    localStorage.setItem('user', JSON.stringify(data.user))
    return data
  }

  public async generateInviteLink(userId: string): Promise<{ invite_url: string }> {
    return this.post<{ invite_url: string }>(`/api/admin/users/${userId}/invite`, {})
  }

  public async getEmailTemplate(key: string): Promise<EmailTemplateData> {
    return this.get<EmailTemplateData>(`/api/admin/email-templates/${encodeURIComponent(key)}`)
  }

  public async updateEmailTemplate(
    key: string,
    payload: { subject: string; body_text: string; body_html: string }
  ): Promise<EmailTemplateData> {
    return this.request<EmailTemplateData>(`/api/admin/email-templates/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  public async logout(): Promise<void> {
    try {
      if (this.accessToken) {
        await this.request('/api/auth/logout', { method: 'POST' });
      }
    } catch (error) {
      // Ignore logout errors
    } finally {
      this.clearTokens();
    }
  }
}

export const apiClient = new ApiClient();
